// Degree ladder for coach profiles (migration 0022).
//
// Six fixed strings, lowest to highest. These are STORED VERBATIM — the
// apostrophes in "Bachelor's Degree" / "Master's Degree" and the period in
// "Ph.D." are part of the value, not display sugar. The server validates
// against this exact list in PUT /api/coach, so changing a string here without
// a data migration would orphan every row holding the old spelling.
//
// Distinct from `coaches.education`, which is the free-text SCHOOL /
// INSTITUTION field and is untouched by 0022.
//
// The empty selection is meaningful: NULL means "not stated", never
// "Bachelor's". See the migration header for why there is no default.

export const EDUCATION_LEVELS: readonly string[] = [
  'High School Diploma or GED',
  'Vocational or Trade Certificate',
  'Associate Degree',
  "Bachelor's Degree",
  "Master's Degree",
  'Doctorate or Ph.D.',
]

/**
 * Fold the apostrophe variants that look identical to a human onto the plain
 * ASCII U+0027 the ladder is written with.
 *
 * "Bachelor's Degree" typed in a word processor, pasted out of a Google Doc, or
 * copied off a rendered page carries U+2019 RIGHT SINGLE QUOTATION MARK, not
 * U+0027. It renders the same and fails `isValidEducationLevel` outright — a
 * 400 on a value that is, to the person who wrote it, exactly right. U+02BC and
 * U+00B4 show up from the same sort of accident.
 *
 * Unreachable from the <select>, which only ever emits canonical strings, so
 * this exists for hand-written data scripts and hand-built requests.
 *
 * Callers must store the NORMALIZED result. Storing the curly form would leave
 * a row that no longer matches the ladder — the exact orphaning this file's
 * header warns about.
 */
export function normalizeEducationLevel(value: string): string {
  // Escapes, not literals: U+2019 / U+02BC / U+00B4 are visually
  // indistinguishable from U+0027 in most editors, so spelling them out keeps
  // this line reviewable and immune to an encoding round-trip.
  return value.replace(/[\u2019\u02bc\u00b4]/g, "'")
}

/**
 * Server-side enum guard. Empty/absent is valid — it clears the column.
 *
 * Deliberately NOT loosened to do its own normalizing: an off-ladder string
 * still returns false. Callers normalize first, then validate, so the guard
 * stays a strict membership test over EDUCATION_LEVELS.
 */
export function isValidEducationLevel(value: string): boolean {
  return EDUCATION_LEVELS.includes(value)
}

/**
 * Options for the level <select>, given whatever is stored.
 *
 * A stored value that is not on the ladder is prepended so it renders instead
 * of silently blanking — same rule as countries.ts and languages.ts. There is
 * no CHECK constraint, so a row can hold anything a direct SQL write put there.
 */
export function educationLevelOptions(
  current: string | null | undefined,
): readonly string[] {
  if (!current) return EDUCATION_LEVELS
  return EDUCATION_LEVELS.includes(current)
    ? EDUCATION_LEVELS
    : [current, ...EDUCATION_LEVELS]
}
